import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  ProjectDetail,
  ProjectListItem,
  CreateProjectPayload,
  PaginatedResponse,
  TimelineItem,
  UpdateProjectPayload,
} from "../types";

const PROJECTS_KEY = ["projects"] as const;

export function useProjectList(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    companyId?: string;
    clientCompanyId?: string;
    productId?: string;
    bypassScope?: boolean;
  } = {},
): UseQueryResult<PaginatedResponse<ProjectListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PROJECTS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<ProjectListItem>>("/projects", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          type: params.type,
          companyId: params.companyId,
          clientCompanyId: params.clientCompanyId,
          productId: params.productId,
          bypassScope: params.bypassScope ? "true" : undefined,
        },
      }),
  });
}

export function useProject(id: string): UseQueryResult<ProjectDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PROJECTS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ project: ProjectDetail }>(
        `/projects/${id}`,
        { token: accessToken },
      );
      return res.project;
    },
  });
}

export function useCreateProject() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProjectPayload) =>
      apiFetch<{ project: ProjectDetail }>("/projects", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROJECTS_KEY] });
      // Also invalidate companies and products since their counts change
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateProject(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateProjectPayload) =>
      apiFetch<{ project: ProjectDetail }>(`/projects/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROJECTS_KEY] });
    },
  });
}

export function useDeleteProject() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/projects/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...PROJECTS_KEY] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useProjectSubEntity<T>(
  projectId: string,
  entity: string,
  params: Record<string, string | number | undefined> = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PROJECTS_KEY, projectId, entity, params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!projectId,
    queryFn: () =>
      apiFetch<PaginatedResponse<T>>(`/projects/${projectId}/${entity}`, {
        token: accessToken,
        query: params as Record<string, string | number>,
      }),
  });
}

export function useProjectTimeline(
  projectId: string,
  params: {
    page?: number;
    limit?: number;
    types?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  } = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PROJECTS_KEY, projectId, "timeline", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!projectId,
    queryFn: () =>
      apiFetch<{ data: TimelineItem[]; total: number }>(
        `/projects/${projectId}/timeline`,
        {
          token: accessToken,
          query: {
            page: params.page,
            limit: params.limit,
            types: params.types,
            dateFrom: params.dateFrom,
            dateTo: params.dateTo,
            search: params.search,
          },
        },
      ),
  });
}
